import { useQuery } from '@tanstack/react-query';
import { API } from 'api';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';

import { apiName, apiBasePath } from 'assets';

export type RetestInfoResponse = {
  resampleMonth: string | null;
  retestWindow: number | null;
} | null;

export const getRetestInfo = async (
  kitId: string,
): Promise<RetestInfoResponse> => {
  const response = await API.get(
    apiName,
    `${apiBasePath}/results/retest-info/${kitId}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.RESULTS,
        importance: IMPORTANCE.MEDIUM,
      },
      context: {
        kitId,
      },
    },
  );
  return response.result;
};

export const getRetestInfoQuery = (kitId?: string | null) => ({
  queryKey: [apiName, 'retestInfo', kitId],
  queryFn: () => (kitId ? getRetestInfo(kitId) : null),
});

// Deliberately not a suspense query: retest data is optional, and an
// unavailable endpoint (deploy skew, backend error) must degrade to "no retest
// slide" rather than throw up to AppErrorBoundary and blank the results page.
export const useRetestInfo = (kitId?: string | null) => {
  const retestInfoQuery = useQuery({
    ...getRetestInfoQuery(kitId),
    enabled: !!kitId,
    retry: false,
  });
  return {
    ...retestInfoQuery,
    resampleMonth: retestInfoQuery.data?.resampleMonth ?? null,
    retestWindow: retestInfoQuery.data?.retestWindow ?? null,
  };
};

export default useRetestInfo;
